// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — collab category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "collab" — realtime-collaboration session commands.
//
//   • LOCK    — soft-lock a selected entity or a named layer so OTHER
//               real-time collaborators (the node-companion `/collab/ws`
//               channel, `apps/node-companion/src/collabServer.ts`) cannot
//               edit it. Bare `LOCK` targets the current selection
//               (entity-scope locks); `LOCK LAYER <name>` targets a named
//               layer (layer-scope lock, case-insensitive lookup); `LOCK
//               LIST` / `LOCK ?` lists every currently-held lock from the
//               local live snapshot (no network round trip). Dispatches
//               `collab/lock-request` (bare / LAYER) or `collab/list-locks`
//               (LIST / ?) — `routeLayoutCommand` (editorStore.ts).
//   • UNLOCK  — release a soft lock this tab previously acquired. Bare
//               `UNLOCK` targets the current selection; `UNLOCK LAYER
//               <name>` targets a named layer. Dispatches
//               `collab/lock-release`.
//
// Sources of truth: `apps/node-companion/src/collabTypes.ts` (`LockScope` /
// `LockState` / `LockProposalOutcome` — the wire model this command surface
// mirrors client-side in `apps/web/src/app/collab/collabLockTypes.ts`),
// `apps/node-companion/src/collabServer.ts` (`CollabBroker` — the `/collab/
// ws` broker these commands talk to over `collabLockClient.ts`),
// `editorStore.ts` `routeLayoutCommand` (the CLI-intercept + the
// fail-closed EDIT-veto call site in `dispatch()`).
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_SHELL_UI = "apps/web/src/app/state/shellUiReducer.ts" as const;

const LOCK: CommandDescriptor = {
  id: "LOCK",
  category: "collab",
  status: "implemented",
  // aiCallable:false — LOCK/UNLOCK are COLLABORATION-SESSION-LIFECYCLE
  // commands (mirrors the `aiCallable` JSDoc's BEDIT/CHPROP/PROPERTIES
  // reasoning in types.ts): a soft lock changes what OTHER real humans in
  // the same session may edit, so it must never be driven headlessly by
  // the AI drafting bot through the safe contour — only a real user's
  // explicit CLI/UI action may acquire or release one.
  aiCallable: false,
  aliases: { en: ["LOCK"], ru: ["БЛОКИР"] },
  // Bare/LAYER route to `collab/lock-request`; LIST/? route to the
  // read-only `collab/list-locks` query — both literals are owned by this
  // ONE descriptor (mirrors how LAYOUT owns multiple sub-keyword literals).
  commandTypeLiterals: ["collab/lock-request", "collab/list-locks"],
  parameters: [
    {
      name: "subCommand",
      type: "enum",
      enumValues: ["LAYER", "LIST", "?"],
      required: false,
      description: {
        ru: "Подкоманда: LAYER <имя> (заблокировать слой), LIST или ? (список текущих блокировок). Без подкоманды блокируется текущая выборка.",
        en: "Sub-command: LAYER <name> (lock a layer), LIST or ? (list currently held locks). With no sub-command, the current selection is locked.",
      },
    },
    {
      name: "layerName",
      type: "string",
      required: false,
      description: {
        ru: "Имя слоя для LOCK LAYER. Поиск регистронезависимый через тот же accessor, что используют другие команды чтения слоёв.",
        en: "Layer name for LOCK LAYER. Case-insensitive lookup, via the same accessor other layer-reading commands use.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Мягко блокирует выбранный объект или именованный слой для других участников совместной сессии.",
      long: "Команда БЛОКИР (LOCK) — мягкая (advisory) блокировка realtime-совместной работы (companion-канал /collab/ws). Без аргументов блокирует каждый объект текущей выборки (блокировка уровня объекта — по DXF handle). `LOCK LAYER <имя>` / `БЛОКИР СЛОЙ <имя>` блокирует именованный слой целиком (регистронезависимый поиск); попытка ДРУГОГО участника отредактировать объект на заблокированном слое отклоняется откатом всей команды (fail-closed). `LOCK LIST` / `LOCK ?` / `БЛОКИР СПИСОК` выводит список всех текущих блокировок (тип, цель, владелец) из локального живого снепшота — без сетевого запроса; при отсутствии подключения к сессии выводится информативное сообщение, а не тихий no-op. Блокировка — АРЕНДА (lease) с автопродлением активностью владельца; при обрыве соединения владельца блокировка автоматически снимается сервером (companion), так что упавшая вкладка никогда не блокирует объект навечно. Подтверждение (granted/denied) приходит асинхронно после запроса; факт отправки запроса подтверждается немедленно в командной строке.",
      examples: [
        "LOCK",
        "LOCK LAYER Стены",
        "LOCK LIST",
        "БЛОКИР",
        "БЛОКИР СЛОЙ Walls",
        "БЛОКИР СПИСОК",
      ],
      seeAlso: ["UNLOCK"],
    },
    en: {
      short:
        "Soft-locks a selected entity or a named layer so other realtime-collaboration participants cannot edit it.",
      long: "The LOCK command is an ADVISORY (soft) lock for realtime collaboration (the companion `/collab/ws` channel). With no argument it locks every entity in the current selection (entity-scope lock, by DXF handle). `LOCK LAYER <name>` locks a whole named layer (case-insensitive lookup); an attempt by ANOTHER participant to edit an entity on a locked layer is rejected fail-closed, the WHOLE command atomically reverted. `LOCK LIST` / `LOCK ?` lists every currently held lock (kind, target, owner) from the local live snapshot — no network round trip; when not connected to a collaboration session it reports that explicitly rather than silently no-oping. A lock is a LEASE with owner-activity auto-renewal; if the owner's connection drops the companion server auto-releases it, so a crashed tab can never wedge a lock forever. The grant/deny confirmation arrives asynchronously after the request; the request's submission is acknowledged immediately on the command line.",
      examples: [
        "LOCK",
        "LOCK LAYER Walls",
        "LOCK LIST",
        "LOCK ?",
      ],
      seeAlso: ["UNLOCK"],
    },
  },
  uiMetadata: {
    // Category B (COLLAB-LOCK-COMMAND) — CLI-only, no ribbon surface yet
    // (the deferred S-COLLAB-UI slice owns any future visual affordance).
    labelKeyRu: "command.collab.lock.label",
    labelKeyEn: "command.collab.lock.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "collab/lock-request", file: REDUCER_SHELL_UI },
    { commandTypeLiteral: "collab/list-locks", file: REDUCER_SHELL_UI },
  ],
};

const UNLOCK: CommandDescriptor = {
  id: "UNLOCK",
  category: "collab",
  status: "implemented",
  // aiCallable:false — same collaboration-session-lifecycle reasoning as LOCK.
  aiCallable: false,
  aliases: { en: ["UNLOCK"], ru: ["РАЗБЛОК"] },
  commandTypeLiterals: ["collab/lock-release"],
  parameters: [
    {
      name: "subCommand",
      type: "enum",
      enumValues: ["LAYER"],
      required: false,
      description: {
        ru: "Подкоманда: LAYER <имя> (разблокировать слой). Без подкоманды разблокируется текущая выборка.",
        en: "Sub-command: LAYER <name> (release a layer's lock). With no sub-command, the current selection is released.",
      },
    },
    {
      name: "layerName",
      type: "string",
      required: false,
      description: {
        ru: "Имя слоя для UNLOCK LAYER. Тот же регистронезависимый accessor, что и LOCK LAYER.",
        en: "Layer name for UNLOCK LAYER. Same case-insensitive accessor as LOCK LAYER.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Снимает мягкую блокировку с выбранного объекта или именованного слоя.",
      long: "Команда РАЗБЛОК (UNLOCK) снимает advisory-блокировку, установленную командой LOCK. Без аргументов освобождает блокировки всех объектов текущей выборки; `UNLOCK LAYER <имя>` / `РАЗБЛОК СЛОЙ <имя>` освобождает блокировку именованного слоя (регистронезависимый поиск). Companion-сервер не подтверждает освобождение индивидуально — успех определяется по следующему широковещательному обновлению блокировок (lock-update); если освобождение не оказало эффекта (например, блокировка принадлежит другому участнику или уже отсутствует), после короткого таймаута выводится соответствующее сообщение, а не бесконечное ожидание.",
      examples: ["UNLOCK", "UNLOCK LAYER Стены", "РАЗБЛОК", "РАЗБЛОК СЛОЙ Walls"],
      seeAlso: ["LOCK"],
    },
    en: {
      short: "Releases a soft lock previously acquired with LOCK.",
      long: "The UNLOCK command releases an advisory lock acquired via LOCK. With no argument it releases every entity in the current selection; `UNLOCK LAYER <name>` releases a named layer's lock (case-insensitive lookup). The companion server does not ack a release individually — success is inferred from the next broadcast lock-update; if the release had no observed effect (e.g. the lock is held by another participant, or was already absent) a bounded timeout reports that outcome rather than waiting forever.",
      examples: ["UNLOCK", "UNLOCK LAYER Walls"],
      seeAlso: ["LOCK"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.collab.unlock.label",
    labelKeyEn: "command.collab.unlock.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "collab/lock-release", file: REDUCER_SHELL_UI },
  ],
};

export const COLLAB_DESCRIPTORS: readonly CommandDescriptor[] = [LOCK, UNLOCK];
